import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { IconChevronRight, IconPrescription, IconX } from "@tabler/icons-react";
import { documentService } from "@/services/documents.service";
import { getMemberInfo } from "@/hooks/useMembers";
import { PillboxPendingChange } from "@/types/pillbox";
import { formatDisplayDate, formatShortDate } from "./utils/formatMedicine";
import styles from "./ViewPrescriptions.module.css";

interface PrescriptionSummary {
  sourcePrescriptionId: string;
  sourceDocumentId: string;
  doctorName?: string;
  dateOfVisit?: string;
}

const ViewPrescriptions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";

  const [openingId, setOpeningId] = useState<string | null>(null);

  const prescriptions = useMemo<PrescriptionSummary[]>(() => {
    const pending = (location.state?.pending as PillboxPendingChange[]) ?? [];
    const map = new Map<string, PrescriptionSummary>();
    for (const item of pending) {
      if (!map.has(item.sourcePrescriptionId)) {
        map.set(item.sourcePrescriptionId, {
          sourcePrescriptionId: item.sourcePrescriptionId,
          sourceDocumentId: item.sourceDocumentId,
          doctorName: item.doctorName,
          dateOfVisit: item.dateOfVisit,
        });
      }
    }
    return Array.from(map.values());
  }, [location.state]);

  const handleOpen = async (prescription: PrescriptionSummary) => {
    if (openingId) return;
    setOpeningId(prescription.sourcePrescriptionId);
    try {
      const document = await documentService(memberId).getById(
        prescription.sourceDocumentId
      );
      navigate("/documents/view", {
        state: {
          document,
          backUrl: "/pillbox/review/prescriptions",
          // This page renders purely from location.state.pending with no
          // API refetch — re-attach it so the list survives the back nav
          // from the document viewer (see useBackNavigation's backState).
          backState: { pending: location.state?.pending },
        },
      });
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => navigate("/pillbox/review")}
            aria-label={t("common.close")}
          >
            <IconX size={24} />
          </button>
        </div>
        <div className={styles.textHeader}>
          <h1 className={styles.title}>
            {t("pillbox.review.prescriptionsTitle")}
          </h1>
          <p className={styles.subtitle}>
            {t("pillbox.review.prescriptionsSubtitle")}
          </p>
        </div>
      </div>

      <div className={styles.list}>
        {prescriptions.map((prescription) => (
          <button
            type="button"
            key={prescription.sourcePrescriptionId}
            className={styles.card}
            onClick={() => handleOpen(prescription)}
            disabled={openingId === prescription.sourcePrescriptionId}
          >
            <div className={styles.cardContent}>
              <span className={styles.doctorDate}>
                {[
                  prescription.doctorName,
                  prescription.dateOfVisit &&
                    formatShortDate(prescription.dateOfVisit),
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </span>
              <span className={styles.metaRow}>
                <span className={styles.metaLabel}>
                  <IconPrescription size={12} />
                  {t("pillbox.review.prescriptionWord")}
                </span>
                {prescription.dateOfVisit && (
                  <span className={styles.metaDate}>
                    {t("pillbox.review.prescriptionDated", {
                      date: formatDisplayDate(prescription.dateOfVisit),
                    })}
                  </span>
                )}
              </span>
            </div>
            <IconChevronRight size={24} className={styles.chevron} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ViewPrescriptions;
